/**
 * Billing service for client-side API calls
 */
import apiService from './apiService';

const billingService = {
  /**
   * Get all billing templates
   * @returns {Promise<Array>} - Array of billing templates
   */
  async fetchBillingTemplates() {
    try {
      return await apiService.get('/billing/templates');
    } catch (error) {
      console.error('Error fetching billing templates:', error);
      throw error;
    }
  },

  /**
   * Get a specific billing template
   * @param {string} id - Template ID
   * @returns {Promise<Object>} - Billing template data
   */
  async fetchBillingTemplate(id) {
    try {
      return await apiService.get(`/billing/templates/${id}`);
    } catch (error) {
      console.error(`Error fetching billing template ${id}:`, error);
      throw error;
    }
  },

  /**
   * Calculate billing for a site
   * @param {number|string} siteId - Site ID
   * @param {Object} options - Calculation options
   * @returns {Promise<Object>} - Billing calculation results
   */
  async calculateSiteBilling(siteId, options = {}) {
    try {
      return await apiService.get(`/billing/sites/${siteId}`, { params: options });
    } catch (error) {
      console.error(`Error calculating billing for site ${siteId}:`, error);
      throw error;
    }
  },

  async calculateAccountBilling(accountId, options = {}) {
    try {
      return await apiService.get(`/billing/accounts/${accountId}`, { params: options });
    } catch (error) {
      console.error(`Error calculating billing for account ${accountId}:`, error);
      throw error;
    }
  },

  /**
   * Calculate billing for a period
   * @param {Object} data - Calculation parameters
   * @returns {Promise<Object>} - Billing calculation results
   */
  async calculateBilling(data) {
    try {
      return await apiService.post('/billing/calculate', data);
    } catch (error) {
      console.error('Error calculating billing:', error);
      throw error;
    }
  },

  /**
   * Export billing data to CSV
   * @param {Object} data - Export parameters
   * @returns {Promise<Object>} - Export results
   */
  async exportToCsv(data) {
    try {
      return await apiService.post('/billing/export', data);
    } catch (error) {
      console.error('Error exporting billing data:', error);
      throw error;
    }
  },

  async createBillingTemplate(data) {
    return await apiService.post('/billing/templates', data);
  },

  async updateBillingTemplate(key, data) {
    return await apiService.put(`/billing/templates/${encodeURIComponent(key)}`, data);
  },

  async deleteBillingTemplate(key) {
    return await apiService.delete(`/billing/templates/${encodeURIComponent(key)}`);
  },

  async duplicateBillingTemplate(key, data = {}) {
    return await apiService.post(`/billing/templates/${encodeURIComponent(key)}/duplicate`, data);
  }
};

export default billingService;
